/**
 * An offset into an identified sequence of bytes. The can be used to determine
 * whether two offsets refer to the same sequence of bytes, and thereby
 * determining whether the difference in position between the offsets have any
 * meaning as a byte length measurement.
 */
export class Offset {
  constructor(readonly id: string | null, readonly position: number) {}

  add(count: number) {
    return new Offset(this.id, this.position + count);
  }
}

/**
 * A restricted view into some underlying provider of bytes. This interface may
 * be backed by anything, including Node.js file handles, browser blobs and
 * plain array buffers.
 *
 * This abstraction allows us to write parsing and I/O code that works equally
 * well in browser and Node.js execution environments.
 */
export interface BlobView {
  arrayBuffer(): Promise<ArrayBuffer>;
  offset(): Offset;
  slice(start?: number, end?: number): BlobView;
  tryExtend(after: BlobView): BlobView | null;
  size?: number;
}

/**
 * A blob view backed by a plain ArrayBuffer.
 */
export class BufferBlob implements BlobView {
  constructor(readonly buffer: ArrayBuffer, private start: Offset) {}

  arrayBuffer(): Promise<ArrayBuffer> {
    return Promise.resolve(this.buffer);
  }

  offset(): Offset {
    return this.start;
  }

  get size(): number {
    return this.buffer.byteLength;
  }

  slice(start?: number, end?: number): BlobView {
    const s = start ?? 0;
    return new BufferBlob(this.buffer.slice(s, end), this.start.add(s));
  }

  tryExtend(): BlobView | null {
    return null;
  }
}
